import React, { useEffect, useState, useContext } from 'react';
import { Typography, Box, Card, Grid, Divider } from '@mui/joy';
import { AuthContext } from '../../context/AuthContext.jsx';
import ClientAnalytics from '../components/ClientAnalytics';
import ClientPermitsTable from '../components/ClientPermitsTable';
import ClientDocuments from '../components/ClientDocuments';
import ClientGISProjects from '../components/ClientGISProjects';
import ClientSubmittals from '../components/ClientSubmittals';

const ClientHome = () => {
  const { user } = useContext(AuthContext);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'transparent',
        position: 'relative'
      }}
    >
      {/* Welcome Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.08) 0%, rgba(0, 126, 51, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'success.200',
          p: 4,
          mb: 3,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
        className="fade-in"
      >
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid xs={12} md={8}>
              <Typography 
                level="h2" 
                sx={{ 
                  color: 'success.700',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
                  dashboard
                </span>
                {getGreeting()}!
              </Typography>
              <Typography level="body-lg" sx={{ color: 'text.secondary', mb: 2 }}>
                Welcome to your project dashboard. Here's an overview of your current permits, submittals, and project status.
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                {formatDate(currentTime)}
              </Typography>
            </Grid>
            <Grid xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
              <Card
                sx={{
                  p: 2,
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid',
                  borderColor: 'success.200',
                  boxShadow: '0 4px 20px rgba(0, 168, 132, 0.1)',
                }}
              >
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  Current Status
                </Typography>
                <Typography level="title-sm" sx={{ color: 'success.600', fontWeight: 600 }}>
                  🟢 All Systems Operational
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ px: 4, pb: 4, maxWidth: '1400px', mx: 'auto' }}>
        {/* Analytics Section */}
        <Box className="fade-in-up" sx={{ mb: 4 }}>
          <ClientAnalytics />
        </Box>

        {/* Content Sections */}
        <Grid container spacing={4}>
          {/* Permits Section */}
          <Grid xs={12}>
            <Card
              id="permits"
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(15px)',
                border: '1px solid',
                borderColor: 'success.200',
                boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
                borderRadius: 'xl',
                overflow: 'hidden',
              }}
              className="fade-in-up"
            >
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'success.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 600
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    assignment
                  </span>
                  Permits & Records
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Track your permit applications and approval status
                </Typography>
              </Box>
              <ClientPermitsTable />
            </Card>
          </Grid>

          {/* Submittals Section */}
          <Grid xs={12}>
            <Card
              id="client-submittals"
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(15px)',
                border: '1px solid',
                borderColor: 'success.200',
                boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
                borderRadius: 'xl',
                overflow: 'hidden',
              }}
              className="fade-in-up"
            >
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'success.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 600
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    upload_file
                  </span>
                  Submittals
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Monitor submittal progress and review status
                </Typography>
              </Box>
              <ClientSubmittals />
            </Card>
          </Grid>

          {/* Documents Section */}
          <Grid xs={12}>
            <Card
              id="documents"
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(15px)',
                border: '1px solid',
                borderColor: 'success.200',
                boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
                borderRadius: 'xl',
                overflow: 'hidden',
              }}
              className="fade-in-up"
            >
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'success.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 600
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    folder
                  </span>
                  Project Documents
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Access and download project-related documents
                </Typography>
              </Box>
              <ClientDocuments />
            </Card>
          </Grid>

          {/* GIS Maps Section */}
          <Grid xs={12}>
            <Card
              id="gis"
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(15px)',
                border: '1px solid',
                borderColor: 'success.200',
                boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
                borderRadius: 'xl',
                overflow: 'hidden',
              }}
              className="fade-in-up"
            >
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'success.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 600
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    map
                  </span>
                  Interactive Maps
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  View project locations and GIS mapping data
                </Typography>
              </Box>
              <ClientGISProjects />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ClientHome;